/**
 * ExternalLinkHandler - Utility for enhancing links
 *
 * Adds target="_blank" and security attributes to:
 * 1. All external links
 * 2. Same-site links that end with file extensions
 */

// Define interface for link enhancement options
interface ExternalLinkOptions {
  addNoopener?: boolean;
  addNoreferrer?: boolean;
  addNofollow?: boolean;
  customRelAttributes?: string[];
  fileExtensions?: string[];
  treatSubdomainsAsInternal?: boolean;
}

export class ExternalLinkHandler {
  private static instance: ExternalLinkHandler | null = null;
  private isInitialized = false;
  private links: HTMLAnchorElement[] = [];
  private options: ExternalLinkOptions;
  private defaultFileExtensions = [
      '.pdf', '.doc', '.docx', '.xls', '.xlsx',
      '.ppt', '.pptx', '.zip', '.rar', '.7z',
      '.tar', '.gz', '.mp3', '.mp4', '.avi',
      '.mov', '.wmv', '.csv', '.txt', '.rtf'
  ];

  // Private constructor prevents direct construction calls with 'new'
  private constructor(options: ExternalLinkOptions = {}) {
      // Set default options
      this.options = {
          addNoopener: options.addNoopener !== false, // true by default
          addNoreferrer: options.addNoreferrer !== false, // true by default
          addNofollow: options.addNofollow ?? false, // false by default
          customRelAttributes: options.customRelAttributes || [],
          fileExtensions: options.fileExtensions || this.defaultFileExtensions,
          treatSubdomainsAsInternal: options.treatSubdomainsAsInternal ?? false,
      };
  }

  // Static method to get the instance (Singleton pattern)
  public static getInstance(options?: ExternalLinkOptions): ExternalLinkHandler {
      if (!ExternalLinkHandler.instance) {
          ExternalLinkHandler.instance = new ExternalLinkHandler(options);
      }
      return ExternalLinkHandler.instance;
  }

  /**
   * Initialize the link handler
   */
  public init(options?: ExternalLinkOptions): void {
      // Merge new options with existing options if provided
      if (options) {
          this.options = {
              addNoopener: options.addNoopener !== false, // true by default
              addNoreferrer: options.addNoreferrer !== false, // true by default
              addNofollow: options.addNofollow ?? false, // false by default
              customRelAttributes: options.customRelAttributes || [],
              fileExtensions: options.fileExtensions || this.options.fileExtensions,
              treatSubdomainsAsInternal: options.treatSubdomainsAsInternal ?? false,
          };
      }

      if (this.isInitialized) {
          return;
      }

      // Get all links on the page
      this.links = Array.from(document.querySelectorAll('a'));

      // Process each link
      if (this.links.length) {
          this.links.forEach(link => this.processLink(link));
      }

      this.isInitialized = true;
  }

  /**
   * Check if a URL ends with a file extension
   * @param url - URL to check
   * @returns boolean - true if URL ends with a file extension
   */
  private hasFileExtension(url: string): boolean {
      // Remove query parameters and hash fragments
      const cleanUrl = url.split('?')[0].split('#')[0];

      // Check if the URL ends with any of the specified file extensions
      return this.options.fileExtensions?.some(ext =>
          cleanUrl.toLowerCase().endsWith(ext.toLowerCase())
      ) || false;
  }

  /**
   * Process a link to add attributes if needed
   * @param link - The anchor element to process
   */
  private processLink(link: HTMLAnchorElement): void {
      try {
          // Get the href attribute
          const href = link.getAttribute('href');

          // Skip if no href or it's a javascript: or mailto: link
          if (!href || href.startsWith('javascript:') ||
              href.startsWith('mailto:') || href.startsWith('#')) {
              return;
          }

          // Get the current hostname to compare against
          const currentHostname = window.location.hostname;

          // Create a URL object to parse the href
          const urlObj = new URL(href, window.location.origin);

          let isExternalLink = urlObj.hostname !== currentHostname && urlObj.hostname !== '';

          // Handle subdomains
          if (this.options.treatSubdomainsAsInternal) {
              const currentDomain = currentHostname.split('.').slice(-2).join('.'); // Get base domain
              const linkDomain = urlObj.hostname.split('.').slice(-2).join('.');   // Get base domain

              if (linkDomain === currentDomain) {
                  isExternalLink = false;  // Treat subdomain as internal
              }
          }


          // Check conditions to open in new tab:
          // 1. External link (different hostname)
          // 2. Same-site link that ends with a file extension
          const isFileLink = this.hasFileExtension(urlObj.pathname);

          if (isExternalLink || isFileLink) {
              // Add target="_blank" to open in new tab
              link.setAttribute('target', '_blank');

              // Set rel attributes based on options
              const relAttributes: string[] = [];

              if (this.options.addNoopener) {
                  relAttributes.push('noopener');
              }

              if (this.options.addNoreferrer) {
                  relAttributes.push('noreferrer');
              }

              if (this.options.addNofollow) {
                  relAttributes.push('nofollow');
              }

              // Add any custom rel attributes
              if (this.options.customRelAttributes && this.options.customRelAttributes.length) {
                  relAttributes.push(...this.options.customRelAttributes);
              }

              // Get existing rel attribute
              const existingRel = link.getAttribute('rel');
              if (existingRel) {
                  // Add existing rel values that aren't already in our array
                  existingRel.split(' ').forEach(value => {
                      if (!relAttributes.includes(value)) {
                          relAttributes.push(value);
                      }
                  });
              }

              // Set the new rel attribute if we have any values
              if (relAttributes.length) {
                  link.setAttribute('rel', relAttributes.join(' '));
              }
          }
      } catch (e) {
          // Handle invalid URLs silently
          console.error('Error processing link:', link, e);
      }
  }

  /**
   * Process new links that might have been added after initial page load
   * @param container - Optional container element to limit the search scope
   */
  public refreshLinks(container: HTMLElement = document.body): void {
      const newLinks = Array.from(container.querySelectorAll('a'));
      if (newLinks.length) {
          newLinks.forEach(link => this.processLink(link as HTMLAnchorElement));
      }
  }

  /**
   * Add additional file extensions to be opened in a new tab
   * @param extensions - Array of file extensions (starting with '.')
   */
  public addFileExtensions(extensions: string[]): void {
      if (!this.options.fileExtensions) {
          this.options.fileExtensions = [];
      }

      extensions.forEach(ext => {
          // Ensure extension starts with a dot
          const extension = ext.startsWith('.') ? ext : `.${ext}`;

          if (!this.options.fileExtensions?.includes(extension)) {
              this.options.fileExtensions?.push(extension);
          }
      });

      // Re-process all links with updated file extensions
      if (this.isInitialized) {
          this.links.forEach(link => this.processLink(link));
      }
  }
}

// Export a single instance
export const externalLinks = ExternalLinkHandler.getInstance();

// For backward compatibility with import default
export default ExternalLinkHandler.getInstance();
